/*
 * Focus management for dialogs that are shown by toggling CSS.
 *
 * Written for Vargas v. Listen Up (1:26-cv-13881-WGY), complaint para. 34(b)
 * and 34(c): dialogs open without receiving screen-reader focus and without
 * being announced, so a screen-reader user keeps reading the page underneath
 * and never learns the dialog exists.
 *
 * Verified against production on 2026-08-28: adding a product opened the
 * mini-cart with no role, no aria-modal, focus left on the page behind it,
 * and all 79 focusable elements behind the dialog still reachable by Tab.
 *
 * Deliberately framework-free and defensive. The dialogs it manages are
 * rendered by Webflow export code and by third-party apps, so it observes
 * visibility rather than hooking into any open/close API it does not own.
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function isVisible(el) {
    if (!el) return false;
    if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
    var node = el;
    while (node && node.nodeType === 1) {
      var s = getComputedStyle(node);
      if (s.display === 'none' || s.visibility === 'hidden' || s.visibility === 'collapse') return false;
      node = node.parentElement;
    }
    return true;
  }

  function focusableWithin(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isVisible);
  }

  // The last thing the user focused that was not inside a managed dialog.
  //
  // Reading document.activeElement when the dialog opens is not good enough:
  // the cart script blurs the Add to Cart button while it re-renders, so by the
  // time the style mutation reaches our observer the active element is already
  // <body> and we would "restore" focus to nothing. Tracking focusin keeps the
  // real trigger so Escape returns the user where they were.
  var lastExternalFocus = null;
  document.addEventListener(
    'focusin',
    function (e) {
      var el = e.target;
      if (el && el.closest && !el.closest('[data-a11y-dialog]')) {
        lastExternalFocus = el;
      }
    },
    true
  );

  /**
   * @param {Element} dialog        the element that becomes visible
   * @param {Object}  opts
   * @param {string}  opts.label    accessible name for the dialog
   * @param {string}  [opts.closeSelector] selector for the close control
   */
  function manage(dialog, opts) {
    if (!dialog || dialog.__a11yManaged) return;
    dialog.__a11yManaged = true;

    var options = opts || {};
    var lastFocused = null;
    var isOpen = false;
    var focusGuard = null;

    dialog.setAttribute('data-a11y-dialog', '');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    if (options.label && !dialog.getAttribute('aria-label')) {
      dialog.setAttribute('aria-label', options.label);
    }

    // Everything that is not the dialog gets hidden from assistive tech while
    // it is open, so a screen reader cannot wander out of it. Siblings are
    // used rather than a blanket selector so the dialog's own ancestors stay
    // in the tree.
    function backgroundSiblings() {
      var out = [];
      var node = dialog;
      while (node && node !== document.body) {
        var parent = node.parentElement;
        if (!parent) break;
        Array.prototype.forEach.call(parent.children, function (child) {
          if (child !== node) out.push(child);
        });
        node = parent;
      }
      return out;
    }

    function hideBackground(hide) {
      backgroundSiblings().forEach(function (el) {
        if (hide) {
          if (el.hasAttribute('aria-hidden')) {
            el.setAttribute('data-a11y-prev-hidden', el.getAttribute('aria-hidden'));
          }
          el.setAttribute('aria-hidden', 'true');
        } else if (el.hasAttribute('data-a11y-prev-hidden')) {
          el.setAttribute('aria-hidden', el.getAttribute('data-a11y-prev-hidden'));
          el.removeAttribute('data-a11y-prev-hidden');
        } else {
          el.removeAttribute('aria-hidden');
        }
      });
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();

        // Capture the trigger before closing. The visibility observer can fire
        // a spurious close() while the dialog is still being revealed, which
        // clears lastFocused, so Escape must not depend on it.
        var returnTo = lastFocused;

        var closer = options.closeSelector && dialog.querySelector(options.closeSelector);
        if (closer) {
          closer.click();
        }
        close();
        holdFocus(
          function () { return returnTo; },
          function () { return isOpen; }
        );
        return;
      }

      if (e.key !== 'Tab') return;

      var items = focusableWithin(dialog);
      if (!items.length) {
        // Nothing to land on; keep focus on the dialog itself rather than
        // letting Tab escape to the page behind.
        e.preventDefault();
        dialog.focus();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      if (isOpen) return;

      isOpen = true;

      // Prefer the tracked external focus; fall back to activeElement only if
      // nothing has been focused yet this page view.
      var active = document.activeElement;
      lastFocused =
        active && active !== document.body && !dialog.contains(active)
          ? active
          : lastExternalFocus;

      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      hideBackground(true);
      document.addEventListener('keydown', onKeydown, true);

      // Focus the close control if there is one, otherwise the first thing in
      // the dialog. Either way the screen reader is moved inside and announces
      // the dialog instead of carrying on down the page behind it.
      //
      // These dialogs are revealed in stages by code we do not own, so an
      // element can still be unrendered — and silently unfocusable — for
      // several frames after the style mutation that triggered us. It also
      // blurs the document again shortly after, so stopping at the first
      // success is not enough. Hold focus until it settles.
      function entryTarget() {
        return (
          (options.closeSelector && dialog.querySelector(options.closeSelector)) ||
          focusableWithin(dialog)[0] ||
          dialog
        );
      }

      holdFocus(entryTarget, function () {
        return !isOpen; // closed again; stop chasing
      });

      // While the dialog is open, focus must not be outside it. The cart script
      // re-renders and blurs the document at unpredictable points, and a fixed
      // settle window is not enough — it can steal focus long after we stopped
      // watching. Guard for as long as the dialog is actually open. Tab is
      // already trapped by onKeydown, so this only ever corrects a steal, never
      // a deliberate move by the user.
      if (focusGuard) window.clearInterval(focusGuard);
      focusGuard = window.setInterval(function () {
        if (!isOpen) {
          window.clearInterval(focusGuard);
          focusGuard = null;
          return;
        }
        if (!dialog.contains(document.activeElement)) {
          var target = entryTarget();
          if (target && document.contains(target)) {
            try {
              target.focus();
            } catch (err) {
              /* best effort */
            }
          }
        }
      }, 100);
    }

    /**
     * Put focus back and keep it there.
     *
     * The cart script blurs the document during its close animation and again
     * in deferred cleanup afterwards, so a single focus() call gets silently
     * undone and the user is dumped at the top of the page. Re-assert until it
     * holds, frame by frame and then on a few timers.
     */
    /**
     * Put focus on a target and keep it there until it has settled.
     *
     * The cart script blurs the document at several points while it opens and
     * closes, and the timing varies with animation and network speed. A single
     * focus() call — or one that stops at the first success — gets silently
     * undone and the user ends up on <body>. Re-assert until focus has held for
     * a short settle period, with a hard cap so this can never fight the page
     * indefinitely.
     *
     * @param {function(): (Element|null)} resolve returns the element to focus,
     *   re-evaluated each tick so a re-rendered target is picked up
     * @param {function(): boolean} abort stop as soon as this returns true
     */
    function holdFocus(resolve, abort) {
      var SETTLE_MS = 400;
      var MAX_MS = 4000;
      var startedAt = Date.now();
      var heldSince = null;

      function attempt() {
        var target = resolve();
        if (!target || !document.contains(target)) return false;

        if (document.activeElement === target) {
          if (heldSince === null) heldSince = Date.now();
          return Date.now() - heldSince >= SETTLE_MS;
        }

        // Lost or stolen; put it back and restart the settle clock.
        heldSince = null;
        try {
          target.focus();
        } catch (err) {
          /* best effort */
        }
        return false;
      }

      attempt();

      var timer = window.setInterval(function () {
        if (abort() || Date.now() - startedAt > MAX_MS || attempt()) {
          window.clearInterval(timer);
        }
      }, 50);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;

      if (focusGuard) {
        window.clearInterval(focusGuard);
        focusGuard = null;
      }
      hideBackground(false);
      document.removeEventListener('keydown', onKeydown, true);

      var restoreTo = lastFocused;
      lastFocused = null;
      holdFocus(
        function () { return restoreTo; },
        function () { return isOpen; } // reopened; leave focus alone
      );
    }

    // The dialogs here are shown by mutating inline styles or classes, so
    // visibility is observed rather than assumed.
    var observer = new MutationObserver(function () {
      if (isVisible(dialog)) {
        open();
      } else {
        close();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });

    // Some vendor dialogs are removed from the DOM rather than hidden -
    // SureBright's "no protection" handler calls remove() on its modal. A
    // detached element never fires its own mutations, so without watching the
    // parent we would never run close(), leaving the page behind it hidden
    // from assistive technology and focus stranded on a node that is gone.
    if (dialog.parentElement) {
      new MutationObserver(function () {
        if (!document.contains(dialog)) close();
      }).observe(dialog.parentElement, { childList: true });
    }

    if (isVisible(dialog)) open();
  }

  function init() {
    // Mini-cart. Opens on add-to-cart and is the dialog described at
    // complaint para. 34(b), including its "View cart" button.
    var miniCart = document.querySelector('[data-node-type="commerce-cart-container-wrapper"]');
    if (miniCart) {
      manage(miniCart, {
        label: 'Shopping cart',
        closeSelector: '[data-node-type="commerce-cart-close-link"]',
      });
    }

    // How-to-shop modal on collection pages.
    var howToShop = document.querySelector('.how-to-shop-modal');
    if (howToShop) {
      manage(howToShop, { label: 'How to shop', closeSelector: '.modal-close-btn' });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ListenUpA11yDialog = {
    manage: manage,
    focusableWithin: focusableWithin,
  };
})();
